/**
 * Larger editor for adjusting a user's fierros balance.
 *
 * Lets the admin enter:
 *   - mode: "Sumar" (positive delta) or "Restar" (negative delta)
 *   - amount: integer 1..100000
 *   - note: optional free text logged into the ledger row
 *
 * Backed by the same `/api/billing/admin/grant-tokens` endpoint as the
 * inline +/- buttons; we just send a larger signed amount. Validation
 * (no negative final balance) lives on the backend.
 */

import { useEffect, useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Loader2, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FierroIcon } from '@/components/icons/FierroIcon';
import { useAdjustUserTokens } from '@/features/admin/useAdmin';
import type { AdminUser } from '@/features/admin/types';

interface Props {
  user: AdminUser | null;
  onOpenChange: (open: boolean) => void;
}

type Mode = 'add' | 'remove';

export function TokensEditorDialog({ user, onOpenChange }: Props) {
  const adjust = useAdjustUserTokens();
  const [mode, setMode] = useState<Mode>('add');
  const [amount, setAmount] = useState(50);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset state every time we open against a new user so stale values
  // don't carry over from a previous edit.
  useEffect(() => {
    if (user) {
      setMode('add');
      setAmount(50);
      setNote('');
      setError(null);
    }
  }, [user?.id]);

  const open = !!user;
  const currentBalance = user?.tokens_balance ?? 0;
  const signed = mode === 'add' ? amount : -amount;
  const previewBalance = currentBalance + signed;
  const wouldGoNegative = previewBalance < 0;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    setError(null);
    if (amount <= 0) {
      setError('Ingresa una cantidad mayor a cero.');
      return;
    }
    if (wouldGoNegative) {
      setError('El saldo final no puede ser negativo.');
      return;
    }
    try {
      await adjust.mutateAsync({
        user_id: user.id,
        amount: signed,
        note: note.trim(),
      });
      onOpenChange(false);
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (err as any).response?.data?.detail || 'No se pudo ajustar el saldo.'
          : err instanceof Error
            ? err.message
            : 'No se pudo ajustar el saldo.';
      setError(typeof msg === 'string' ? msg : 'No se pudo ajustar el saldo.');
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50"
          style={{ background: 'rgba(2,6,23,0.75)', backdropFilter: 'blur(8px)' }}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-lg)] border p-6 shadow-2xl"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <Dialog.Close
            className="absolute right-3 top-3 rounded-[var(--radius-sm)] p-1.5 transition-colors hover:bg-[var(--surface-2)]"
            aria-label="Cerrar"
          >
            <X size={16} strokeWidth={1.8} />
          </Dialog.Close>

          <div className="mb-4 flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-[var(--radius)]"
              style={{ background: 'rgba(245,158,11,0.18)', color: 'var(--accent)' }}
            >
              <Settings2 size={18} strokeWidth={1.8} />
            </div>
            <div>
              <Dialog.Title className="text-base" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
                Ajustar fierros
              </Dialog.Title>
              <Dialog.Description className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                {user?.email}
              </Dialog.Description>
            </div>
          </div>

          <div
            className="mb-4 grid grid-cols-3 gap-2 rounded-[var(--radius-md)] border p-3 text-center text-[11.5px]"
            style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
          >
            <div>
              <div style={{ color: 'var(--text-muted)' }}>Saldo actual</div>
              <div className="mt-0.5 inline-flex items-center gap-1 font-mono tabular text-lg">
                <FierroIcon size={12} />
                {currentBalance}
              </div>
            </div>
            <div>
              <div style={{ color: 'var(--text-muted)' }}>Δ</div>
              <div
                className="mt-0.5 font-mono tabular text-lg"
                style={{ color: signed >= 0 ? 'var(--accent)' : '#fca5a5' }}
              >
                {signed >= 0 ? `+${signed}` : signed}
              </div>
            </div>
            <div>
              <div style={{ color: 'var(--text-muted)' }}>Nuevo saldo</div>
              <div
                className="mt-0.5 inline-flex items-center gap-1 font-mono tabular text-lg"
                style={{ color: wouldGoNegative ? '#fca5a5' : 'var(--text-primary)' }}
              >
                <FierroIcon size={12} />
                {previewBalance}
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <div className="flex gap-2">
              <ModeButton active={mode === 'add'} onClick={() => setMode('add')}>
                Sumar
              </ModeButton>
              <ModeButton active={mode === 'remove'} onClick={() => setMode('remove')}>
                Restar
              </ModeButton>
            </div>

            <label className="flex flex-col gap-1 text-[12px]" style={{ color: 'var(--text-secondary)' }}>
              Cantidad
              <input
                type="number"
                min={1}
                max={100000}
                value={amount}
                onChange={(e) => setAmount(Number(e.currentTarget.value))}
                className="h-10 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 font-mono tabular text-base"
                style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                required
              />
            </label>

            <label className="flex flex-col gap-1 text-[12px]" style={{ color: 'var(--text-secondary)' }}>
              Nota (opcional)
              <input
                type="text"
                placeholder="Ej. Compensación por bug reportado"
                value={note}
                onChange={(e) => setNote(e.currentTarget.value)}
                className="h-9 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-sm"
                style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
              />
            </label>

            {error && (
              <div
                className="rounded-[var(--radius-sm)] border p-2 text-[12px]"
                style={{
                  background: 'rgba(220,38,38,0.08)',
                  borderColor: 'rgba(220,38,38,0.4)',
                  color: '#fca5a5',
                }}
              >
                {error}
              </div>
            )}

            <div className="mt-1 flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" size="md" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" size="md" disabled={adjust.isPending || wouldGoNegative}>
                {adjust.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
                {mode === 'add' ? 'Sumar fierros' : 'Restar fierros'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 rounded-[var(--radius-sm)] border px-3 py-2 text-sm transition-colors"
      style={{
        background: active ? 'var(--accent)' : 'var(--surface-2)',
        borderColor: active ? 'var(--accent)' : 'var(--border-default)',
        color: active ? 'var(--surface-base)' : 'var(--text-secondary)',
      }}
    >
      {children}
    </button>
  );
}
